import { customElement, property } from "lit/decorators.js";
import { createRef, Ref, ref } from "lit/directives/ref.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { html, TemplateResult } from "lit";
import { debounce, escapeHtml, generateUniqueId } from "@/utilities";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { DropdownOptions, FilterElement, Label } from "@/components/search/filter_element";
import { searchQueryState } from "@/state/SearchQuery";
import { I18n } from "@/i18n/i18n";
import { FilterCollection } from "@/components/search/filter_collection";
import { ClearInitialContentController } from "@/components/meta/clear_initial_content_controller";
/**
 * The labels of one filter that the typeahead should suggest: the ones that match
 * the filter string and are not selected yet.
 */
function matchingLabels(labels: Label[], filter: string, isSelected: (label: Label) => boolean): Label[] {
    if (!filter) return [];

    // No "g" flag: a stateful regex gives different results on repeated test calls.
    const re = new RegExp(RegExp.escape(filter), "i");
    return labels.filter((label) => !isSelected(label) && re.test(label.name));
}

function isDropdownLabelSelected(dropdown: DropdownOptions, label: Label): boolean {
    return dropdown.multi
        ? (searchQueryState.arrayQueryParams.get(dropdown.param) || []).includes(label.id)
        : searchQueryState.queryParams.get(dropdown.param) === label.id;
}

function selectDropdownLabel(dropdown: DropdownOptions, label: Label): void {
    if (dropdown.multi) {
        const selected = searchQueryState.arrayQueryParams.get(dropdown.param) || [];
        searchQueryState.arrayQueryParams.set(dropdown.param, [...selected, label.id]);
    } else {
        searchQueryState.queryParams.set(dropdown.param, label.id);
    }
    searchQueryState.queryParams.set("filter", undefined);
}

/**
 * This component inherits from FilterCollectionElement.
 * It represents a list of filters to be used in a dropdown as typeahead suggestions
 *
 * @element d-search-field-suggestion
 *
 * @prop {string} filter - The string for which typeahead suggestions should be provided
 * @prop {string} param - The searchQuery param to be used for this filter
 * @prop {boolean} multi - Whether one or more labels can be selected at the same time
 * @prop {[Label]} labels - All labels that could potentially be selected
 * @prop {number} offset - The index of the first option of this filter in the full list of options
 * @prop {number} activeIndex - The index of the active option in the full list of options
 * @prop {string} optionIdPrefix - The prefix of the id of every option, completed with its index
 */
@customElement("d-search-field-suggestion")
export class SearchFieldSuggestion extends FilterElement {
    @property()
    filter: string = "";

    @property({ type: Number })
    offset: number = 0;

    @property({ type: Number })
    activeIndex: number = -1;

    @property({ type: String })
    optionIdPrefix: string = "";

    private readonly headerId = `search-suggestion-header-${generateUniqueId()}`;

    getHighlightRegExp(): RegExp {
        return new RegExp(RegExp.escape(this.filter), "gi"); // <-- with "g"
    }

    getFilteredLabels(): Label[] {
        return matchingLabels(this.labels, this.filter, (label) => this.isSelected(label));
    }

    getHighlightedLabel(label: string): string {
        const escaped = escapeHtml(label);
        if (!this.filter) return escaped;
        return escaped.replace(this.getHighlightRegExp(), (str) => `<strong>${str}</strong>`);
    }

    onSuggestionClick(e: Event): void {
        e.preventDefault();

        const el = e.currentTarget as HTMLElement;
        const id = el.dataset.labelId;
        const label = this.labels.find((l) => l.id === id);

        this.select(label);
        searchQueryState.queryParams.set("filter", undefined);
    }

    render(): TemplateResult {
        const filtered = this.getFilteredLabels();

        return filtered.length === 0
            ? html``
            : html`
                  <div role="group" aria-labelledby="${this.headerId}">
                      <h6 id="${this.headerId}" role="presentation" class="dropdown-header">
                          ${I18n.t(`js.search.filter.${this.param}`)}
                      </h6>
                      ${filtered.map((label, i) => {
                          const active = this.offset + i === this.activeIndex;
                          return html`
                              <button
                                  type="button"
                                  role="option"
                                  id="${this.optionIdPrefix}-${this.offset + i}"
                                  aria-selected="${active}"
                                  class="search-item ${active ? "active" : ""}"
                                  tabindex="-1"
                                  data-label-id="${label.id}"
                                  @click=${this.onSuggestionClick}
                              >
                                  <span>${unsafeHTML(this.getHighlightedLabel(label.name))}</span>
                                  ${
                                      label.count
                                          ? html`<span class="text-muted float-end ms-4">${label.count}</span>`
                                          : ""
                                  }
                              </button>
                          `;
                      })}
                  </div>
              `;
    }
}

/**
 * This component represents a searchfield with typeahead suggestion
 * It interacts with SearchQuery to set and get the currently active filters
 *
 * @element d-search-field
 *
 * @prop {string} placeholder - The placeholder text for an empty searchfield
 * @prop {boolean} eager - If true a search will be run before user input happens
 * @prop {FilterOptions[]} filters - The list of filter lists to be used as search suggestions
 * @prop {string[]} hide - The list of filter lists to be ignored as search suggestions
 * @prop {string} filter - The current filter string (Internal)
 * @prop {boolean} hasSuggestions - Whether there are suggestions (Internal, Readonly)
 */
@customElement("d-search-field")
export class SearchField extends FilterCollection {
    clearInitialContent = new ClearInitialContentController(this);

    @property({ type: String })
    placeholder: string = "";

    @property({ state: true })
    filter: string = "";

    @property({ state: true })
    activeIndex: number = -1;

    debouncedSetFilter = debounce(() => searchQueryState.queryParams.set("filter", this.filter), 300);

    inputRef: Ref<HTMLInputElement> = createRef();

    private readonly uniqueId = generateUniqueId();
    private readonly listId = `search-suggestions-${this.uniqueId}`;

    /** The filters that have something to suggest, in the order they render. */
    private get suggestionGroups(): Array<{ dropdown: DropdownOptions; labels: Label[] }> {
        if (!this.visibleDropdowns) return [];

        return this.visibleDropdowns
            .map((dropdown) => ({
                dropdown,
                labels: matchingLabels(dropdown.searchFieldData ?? [], this.filter, (label) =>
                    isDropdownLabelSelected(dropdown, label),
                ),
            }))
            .filter((group) => group.labels.length > 0);
    }

    /** Every suggestion across the groups, which is what activeIndex indexes into. */
    private get suggestions(): Array<{ dropdown: DropdownOptions; label: Label }> {
        return this.suggestionGroups.flatMap(({ dropdown, labels }) => labels.map((label) => ({ dropdown, label })));
    }

    get hasSuggestions(): boolean {
        return this.suggestions.length > 0;
    }

    private get expanded(): boolean {
        return this.hasSuggestions;
    }

    private clearFilter(): void {
        this.debouncedSetFilter.cancel();
        this.filter = "";
        this.activeIndex = -1;
        searchQueryState.queryParams.set("filter", undefined);
        this.inputRef.value?.focus();
    }

    onInput(e: Event): void {
        this.filter = (e.currentTarget as HTMLInputElement).value;
        this.activeIndex = -1;
        this.debouncedSetFilter();
    }

    onKeyDown(e: KeyboardEvent): void {
        if (e.altKey || e.ctrlKey || e.metaKey) return;

        if (e.key === "Escape") {
            e.preventDefault();
            this.clearFilter();
            return;
        }

        const suggestions = this.suggestions;
        if (suggestions.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            this.activeIndex = (this.activeIndex + 1) % suggestions.length;
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            this.activeIndex = (this.activeIndex <= 0 ? suggestions.length : this.activeIndex) - 1;
        } else if (e.key === "Enter" && this.activeIndex >= 0) {
            e.preventDefault();
            const { dropdown, label } = suggestions[this.activeIndex];
            this.clearFilter();
            selectDropdownLabel(dropdown, label);
        }
    }

    protected updated(): void {
        if (this.activeIndex >= 0) {
            this.querySelector(".search-item.active")?.scrollIntoView({ block: "nearest" });
        }
    }

    constructor() {
        super();
    }

    firstUpdated(): void {
        const setFilter = (): string => (this.filter = searchQueryState.queryParams.get("filter") || "");
        searchQueryState.queryParams.subscribe(setFilter, "filter");
        setFilter();
    }

    render(): TemplateResult {
        if (!this.visibleDropdowns) {
            return html``;
        }

        const groups = this.suggestionGroups;
        const expanded = groups.length > 0;
        const activeOptionId = this.activeIndex >= 0 ? `${this.listId}-option-${this.activeIndex}` : undefined;
        let offset = 0;

        return html`
            <input
                type="text"
                role="combobox"
                class="search-filter"
                placeholder=${this.placeholder}
                aria-label=${this.getAttribute("aria-label") ?? this.placeholder}
                aria-controls=${this.listId}
                aria-expanded=${expanded ? "true" : "false"}
                aria-autocomplete="list"
                aria-activedescendant=${ifDefined(activeOptionId)}
                name="filter"
                autocomplete="off"
                .value=${this.filter}
                @input=${this.onInput}
                ${ref(this.inputRef)}
                @keydown=${this.onKeyDown}
            />
            <div
                id=${this.listId}
                class="search-suggestions ${expanded ? "show-search-dropdown" : ""}"
                role="listbox"
                aria-label=${this.getAttribute("aria-label") ?? this.placeholder}
            >
                ${groups.map(({ dropdown, labels }) => {
                    const start = offset;
                    offset += labels.length;

                    return html`
                        <d-search-field-suggestion
                            .labels=${dropdown.searchFieldData ?? []}
                            .filter=${this.filter}
                            .param=${dropdown.param}
                            .multi=${dropdown.multi}
                            .offset=${start}
                            .activeIndex=${this.activeIndex}
                            .optionIdPrefix=${`${this.listId}-option`}
                        >
                        </d-search-field-suggestion>
                    `;
                })}
            </div>
            <button type="submit" tabindex="-1" class="visually-hidden">${this.placeholder}</button>
        `;
    }
}
